import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { handleFeatureNotIncluded } from "../../utils/notifyPlanUpgrade";
import { listMembers, updateProfessionalHours } from "../../services/memberService";
import BusinessHoursEditor from "../../components/BusinessHoursEditor/BusinessHoursEditor";
import { ButtonBack } from "../../components/ui";
import styles from "./ProfessionalHours.module.css";

function Avatar({ name, avatarUrl, size = 36 }) {
  if (avatarUrl) {
    return (
      <img
        src={avatarUrl}
        alt={name}
        style={{ width: size, height: size, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }}
      />
    );
  }
  const initial = name ? name.charAt(0).toUpperCase() : "?";
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        background: "#6e0ad4",
        color: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: "bold",
        fontSize: size * 0.42,
        flexShrink: 0,
      }}
    >
      {initial}
    </div>
  );
}

export default function ProfessionalHours() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [hours, setHours] = useState({});
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const fetchMembers = useCallback(async () => {
    try {
      setLoading(true);
      const res = await listMembers();
      setMembers(res.data);
    } catch {
      toast.error("Erro ao carregar profissionais.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  // Auto-select first member once the list loads
  useEffect(() => {
    if (members.length > 0 && !selectedId) {
      setSelectedId(members[0].userId);
      setHours(members[0].businessHours ?? {});
    }
  }, [members, selectedId]);

  const handleSelectMember = (member) => {
    if (dirty) {
      const ok = window.confirm("Há alterações não salvas. Deseja descartar?");
      if (!ok) return;
    }
    setSelectedId(member.userId);
    setHours(member.businessHours ?? {});
    setDirty(false);
  };

  const handleHoursChange = (newHours) => {
    setHours(newHours);
    setDirty(true);
  };

  const handleSave = async () => {
    if (!selectedId) return;
    setSaving(true);
    try {
      const res = await updateProfessionalHours(selectedId, hours);
      // Reflect persisted data back into members list
      setMembers((prev) =>
        prev.map((m) => (m.userId === selectedId ? { ...m, businessHours: res.data.businessHours } : m))
      );
      setDirty(false);
      toast.success("Expediente salvo com sucesso!");
    } catch (err) {
      // 403 FEATURE_NOT_INCLUDED (Plano Atendimento — ADR-0026): mensagem
      // humana com CTA, nunca erro cru nem a copy genérica.
      if (!handleFeatureNotIncluded(err)) {
        toast.error("Erro ao salvar expediente.");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    const member = members.find((m) => m.userId === selectedId);
    if (member) {
      setHours(member.businessHours ?? {});
      setDirty(false);
    }
  };

  const selectedMember = members.find((m) => m.userId === selectedId);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Expedientes dos Profissionais</h1>
          <p className={styles.subtitle}>
            Defina o horário de trabalho individual de cada profissional
          </p>
        </div>
        <ButtonBack to="/business/professionals" label="← Voltar para Profissionais" />
      </div>

      {loading ? (
        <div className={styles.loading}>Carregando profissionais...</div>
      ) : members.length === 0 ? (
        <div className={styles.empty}>
          Nenhum membro encontrado. Convite membros pela página <strong>Equipe</strong>.
        </div>
      ) : (
        <div className={styles.layout}>
          {/* Left panel — professional selector */}
          <aside className={styles.sidebar}>
            <p className={styles.sidebarTitle}>Profissionais</p>
            <ul className={styles.list}>
              {members.map((m) => (
                <li key={m.id}>
                  <button
                    className={`${styles.memberBtn} ${m.userId === selectedId ? styles.memberBtnActive : ""}`}
                    onClick={() => handleSelectMember(m)}
                  >
                    <Avatar name={m.user?.name} avatarUrl={m.user?.avatarUrl} />
                    <div className={styles.memberInfo}>
                      <span className={styles.memberName}>{m.user?.name ?? "—"}</span>
                      {m.specialty && (
                        <span className={styles.memberSpecialty}>{m.specialty}</span>
                      )}
                      {m.businessHours && Object.keys(m.businessHours).length > 0 ? (
                        <span className={styles.hoursBadge}>
                          {Object.keys(m.businessHours).length}d configurados
                        </span>
                      ) : (
                        <span className={styles.hoursNone}>Sem expediente</span>
                      )}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          </aside>

          {/* Right panel — editor */}
          <div className={styles.editor}>
            {selectedMember ? (
              <>
                <div className={styles.editorHeader}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <Avatar name={selectedMember.user?.name} avatarUrl={selectedMember.user?.avatarUrl} size={44} />
                    <div>
                      <p className={styles.editorName}>{selectedMember.user?.name ?? "—"}</p>
                      {selectedMember.specialty && (
                        <p className={styles.editorSpecialty}>{selectedMember.specialty}</p>
                      )}
                    </div>
                  </div>

                  {dirty && (
                    <span className={styles.unsavedBadge}>• Alterações não salvas</span>
                  )}
                </div>

                <div className={styles.hint}>
                  Deixar todos os dias desativados significa que o profissional segue o
                  horário geral do negócio.
                </div>

                <BusinessHoursEditor hours={hours} onChange={handleHoursChange} />

                <div className={styles.actions}>
                  <button
                    className={styles.resetBtn}
                    onClick={handleReset}
                    disabled={!dirty || saving}
                  >
                    Descartar alterações
                  </button>
                  <button
                    className={styles.saveBtn}
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving ? "Salvando..." : "Salvar expediente"}
                  </button>
                </div>
              </>
            ) : (
              <div className={styles.noSelection}>
                Selecione um profissional para editar o expediente.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
